import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { PatrolService } from '../../services/patrol/patrol.service';
import { PatrolActions } from './patrol.actions';
import { ToastService } from '../../services/toast/toast.service';
import { ToastType } from '../../models';

@Injectable()
export class PatrolEffects {
    private actions$ = inject(Actions);
    private patrolService = inject(PatrolService);
    private toastService = inject(ToastService);

    // -------------------------------------------------------------------
    // Templates
    // -------------------------------------------------------------------

    loadTemplates$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadTemplates),
        switchMap(() =>
            this.patrolService.getTemplates().pipe(
                map(templates => PatrolActions.loadTemplatesSuccess({ templates })),
                catchError(error => of(PatrolActions.loadTemplatesFailure({ error }))),
            ),
        ),
    ));

    createTemplate$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.createTemplate),
        concatMap(({ request }) =>
            this.patrolService.createTemplate(request).pipe(
                map(template => PatrolActions.createTemplateSuccess({ template })),
                catchError(error => {
                    this.toastService.showToast('Failed to create template', ToastType.ERROR);
                    return of(PatrolActions.createTemplateFailure({ error }));
                }),
            ),
        ),
    ));

    updateTemplate$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.updateTemplate),
        concatMap(({ request }) =>
            this.patrolService.updateTemplate(request).pipe(
                map(template => PatrolActions.updateTemplateSuccess({ template })),
                catchError(error => {
                    this.toastService.showToast('Failed to update template', ToastType.ERROR);
                    return of(PatrolActions.updateTemplateFailure({ error }));
                }),
            ),
        ),
    ));

    deleteTemplates$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.deleteTemplates),
        concatMap(({ ids }) =>
            this.patrolService.deleteTemplates(ids).pipe(
                map(() => PatrolActions.deleteTemplatesSuccess({ ids })),
                catchError(error => {
                    this.toastService.showToast('Failed to delete templates', ToastType.ERROR);
                    return of(PatrolActions.deleteTemplatesFailure({ error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Schedules
    // -------------------------------------------------------------------

    loadSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadSchedules),
        switchMap(() =>
            this.patrolService.getSchedules().pipe(
                map(schedules => PatrolActions.loadSchedulesSuccess({ schedules })),
                catchError(error => of(PatrolActions.loadSchedulesFailure({ error }))),
            ),
        ),
    ));

    createSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.createSchedule),
        concatMap(({ request }) =>
            this.patrolService.createSchedule(request).pipe(
                map(schedule => PatrolActions.createScheduleSuccess({ schedule })),
                catchError(error => {
                    this.toastService.showToast('Failed to create schedule', ToastType.ERROR);
                    return of(PatrolActions.createScheduleFailure({ error }));
                }),
            ),
        ),
    ));

    updateSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.updateSchedule),
        concatMap(({ request }) =>
            this.patrolService.updateSchedule(request).pipe(
                map(schedule => PatrolActions.updateScheduleSuccess({ schedule })),
                catchError(error => {
                    this.toastService.showToast('Failed to update schedule', ToastType.ERROR);
                    return of(PatrolActions.updateScheduleFailure({ error }));
                }),
            ),
        ),
    ));

    deleteSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.deleteSchedules),
        concatMap(({ ids }) =>
            this.patrolService.deleteSchedules(ids).pipe(
                map(() => PatrolActions.deleteSchedulesSuccess({ ids })),
                catchError(error => {
                    this.toastService.showToast('Failed to delete schedules', ToastType.ERROR);
                    return of(PatrolActions.deleteSchedulesFailure({ error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Routes
    // -------------------------------------------------------------------

    loadRoutes$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadRoutes),
        switchMap(() =>
            this.patrolService.getRoutes().pipe(
                map(routes => PatrolActions.loadRoutesSuccess({ routes })),
                catchError(error => of(PatrolActions.loadRoutesFailure({ error }))),
            ),
        ),
    ));

    createRoute$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.createRoute),
        concatMap(({ request }) =>
            this.patrolService.createRoute(request).pipe(
                map(route => PatrolActions.createRouteSuccess({ route })),
                catchError(error => {
                    this.toastService.showToast('Failed to create route', ToastType.ERROR);
                    return of(PatrolActions.createRouteFailure({ error }));
                }),
            ),
        ),
    ));

    updateRoute$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.updateRoute),
        concatMap(({ request }) =>
            this.patrolService.updateRoute(request).pipe(
                map(route => PatrolActions.updateRouteSuccess({ route })),
                catchError(error => {
                    this.toastService.showToast('Failed to update route', ToastType.ERROR);
                    return of(PatrolActions.updateRouteFailure({ error }));
                }),
            ),
        ),
    ));

    deleteRoutes$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.deleteRoutes),
        concatMap(({ ids }) =>
            this.patrolService.deleteRoutes(ids).pipe(
                map(() => PatrolActions.deleteRoutesSuccess({ ids })),
                catchError(error => {
                    this.toastService.showToast('Failed to delete routes', ToastType.ERROR);
                    return of(PatrolActions.deleteRoutesFailure({ error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Route Cameras
    // -------------------------------------------------------------------

    loadRouteCameras$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadRouteCameras),
        switchMap(({ routeId }) =>
            this.patrolService.getRouteCameras(routeId).pipe(
                map(cameras => PatrolActions.loadRouteCamerasSuccess({ routeId, cameras })),
                catchError(error => of(PatrolActions.loadRouteCamerasFailure({ error }))),
            ),
        ),
    ));

    loadLicensedCameras$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadLicensedCameras),
        switchMap(({ siteId }) =>
            this.patrolService.getLicensedCameras(siteId).pipe(
                map(cameraIds => PatrolActions.loadLicensedCamerasSuccess({ siteId, cameraIds })),
                catchError(error => of(PatrolActions.loadLicensedCamerasFailure({ siteId, error }))),
            ),
        ),
    ));

    setRouteCameras$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.setRouteCameras),
        concatMap(({ routeId, cameras }) =>
            this.patrolService.setRouteCameras(routeId, cameras).pipe(
                map(result => PatrolActions.setRouteCamerasSuccess({ routeId, cameras: result })),
                catchError(error => {
                    this.toastService.showToast('Failed to save camera assignments', ToastType.ERROR);
                    return of(PatrolActions.setRouteCamerasFailure({ error }));
                }),
            ),
        ),
    ));

    runPatrolRoute$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.runPatrolRoute),
        mergeMap(({ routeId }) =>
            this.patrolService.runRoute(routeId).pipe(
                map(() => {
                    this.toastService.showToast('Patrol dispatched', ToastType.SUCCESS);
                    return PatrolActions.runPatrolRouteSuccess({ routeId });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to dispatch patrol', ToastType.ERROR);
                    return of(PatrolActions.runPatrolRouteFailure({ routeId, error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Runs
    // -------------------------------------------------------------------

    loadRuns$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadRuns),
        switchMap(() =>
            this.patrolService.getRuns().pipe(
                map(runs => PatrolActions.loadRunsSuccess({ runs })),
                catchError(error => of(PatrolActions.loadRunsFailure({ error }))),
            ),
        ),
    ));

    loadRun$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadRun),
        switchMap(({ runId }) =>
            this.patrolService.getRun(runId).pipe(
                map(run => PatrolActions.loadRunSuccess({ run })),
                catchError(error => of(PatrolActions.loadRunFailure({ error }))),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Findings
    // -------------------------------------------------------------------

    loadFindings$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadFindings),
        switchMap(() =>
            this.patrolService.getFindings().pipe(
                map(findings => PatrolActions.loadFindingsSuccess({ findings })),
                catchError(error => of(PatrolActions.loadFindingsFailure({ error }))),
            ),
        ),
    ));

    loadFindingsByRun$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadFindingsByRun),
        switchMap(({ runId }) =>
            this.patrolService.getFindingsByRun(runId).pipe(
                map(findings => PatrolActions.loadFindingsByRunSuccess({ runId, findings })),
                catchError(error => of(PatrolActions.loadFindingsByRunFailure({ runId, error }))),
            ),
        ),
    ));

    acknowledgeFindings$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.acknowledgeFindings),
        concatMap(({ ids }) =>
            this.patrolService.acknowledgeFindings(ids).pipe(
                map(findings => PatrolActions.acknowledgeFindingsSuccess({ findings })),
                catchError(error => {
                    this.toastService.showToast('Failed to acknowledge findings', ToastType.ERROR);
                    return of(PatrolActions.acknowledgeFindingsFailure({ error }));
                }),
            ),
        ),
    ));

    escalateFindings$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.escalateFindings),
        concatMap(({ ids }) =>
            this.patrolService.escalateFindings(ids).pipe(
                map(findings => PatrolActions.escalateFindingsSuccess({ findings })),
                catchError(error => {
                    this.toastService.showToast('Failed to escalate findings', ToastType.ERROR);
                    return of(PatrolActions.escalateFindingsFailure({ error }));
                }),
            ),
        ),
    ));

    commentFindings$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.commentFindings),
        concatMap(({ ids, comment }) =>
            this.patrolService.commentFindings(ids, comment).pipe(
                map(findings => PatrolActions.commentFindingsSuccess({ findings })),
                catchError(error => {
                    this.toastService.showToast('Failed to comment on findings', ToastType.ERROR);
                    return of(PatrolActions.commentFindingsFailure({ error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Data Points
    // -------------------------------------------------------------------

    loadDataPoints$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.loadDataPoints),
        switchMap(({ templateId }) =>
            this.patrolService.getDataPoints(templateId).pipe(
                map(dataPoints => PatrolActions.loadDataPointsSuccess({ templateId, dataPoints })),
                catchError(error => of(PatrolActions.loadDataPointsFailure({ error }))),
            ),
        ),
    ));

    saveDataPoints$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.saveDataPoints),
        concatMap(({ templateId, toUpdate, toAdd, toDelete }) => {
            const operations: Observable<unknown>[] = [];
            toUpdate.forEach((req) => {
                operations.push(this.patrolService.updateDataPoint(req));
            });
            if (toDelete.length > 0) {
                operations.push(this.patrolService.deleteDataPoints(toDelete));
            }
            if (toAdd != null) {
                operations.push(this.patrolService.addDataPoints(toAdd));
            }
            if (operations.length === 0) {
                return of(PatrolActions.saveDataPointsSuccess());
            }
            return forkJoin(operations).pipe(
                map(() => PatrolActions.saveDataPointsSuccess()),
                catchError(error => {
                    this.toastService.showToast('Failed to save data point changes', ToastType.ERROR);
                    return of(PatrolActions.saveDataPointsFailure({ error, templateId }));
                }),
            );
        }),
    ));

    resyncDataPointsOnFailure$ = createEffect(() => this.actions$.pipe(
        ofType(PatrolActions.saveDataPointsFailure),
        map(({ templateId }) => PatrolActions.loadDataPoints({ templateId })),
    ));
}
